'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  ClipboardCheck,
  BookOpen,
  Zap,
  FileText,
  ChevronLeft,
  ChevronRight,
  Settings,
  LogOut,
  Target,
  Bell,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  badgeVariant?: 'primary' | 'warning' | 'accent';
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'nav-dashboard',
    label: 'Dashboard',
    href: '/',
    icon: <LayoutDashboard size={18} />,
  },
  {
    id: 'nav-diagnostic',
    label: 'Diagnostic Test',
    href: '/diagnostic-test',
    icon: <ClipboardCheck size={18} />,
    badge: 'Stage 1',
    badgeVariant: 'primary',
  },
  {
    id: 'nav-walkthrough',
    label: 'Annotated Walkthrough',
    href: '/annotated-walkthrough',
    icon: <BookOpen size={18} />,
    badge: 'Stage 2',
    badgeVariant: 'primary',
  },
  {
    id: 'nav-practice',
    label: 'Practice Session',
    href: '/practice-session',
    icon: <Zap size={18} />,
    badge: '3 due',
    badgeVariant: 'warning',
  },
  {
    id: 'nav-mock',
    label: 'Mock Exam',
    href: '/mock-exam-performance-report',
    icon: <FileText size={18} />,
    badge: 'Stage 4',
    badgeVariant: 'accent',
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  currentPath: string;
}

export default function Sidebar({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onMobileClose,
  currentPath,
}: SidebarProps) {
  const isActive = (href: string) => {
    if (href === '/') return currentPath === '/';
    return currentPath.startsWith(href);
  };

  const badgeClass = (variant?: string) => {
    if (variant === 'warning') return 'bg-warning-bg text-warning text-xs font-semibold px-1.5 py-0.5 rounded-full';
    if (variant === 'accent') return 'bg-success-bg text-accent text-xs font-semibold px-1.5 py-0.5 rounded-full';
    return 'bg-secondary text-primary text-xs font-semibold px-1.5 py-0.5 rounded-full';
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out flex-shrink-0 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-2.5 px-4 py-4 border-b border-border ${collapsed ? 'justify-center px-2' : ''}`}>
          <div className="flex-shrink-0">
            <AppLogo size={32} />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <span className="font-bold text-foreground text-sm block truncate">IELTSNoah</span>
              <span className="text-xs text-muted-foreground">Band 8+ Reading</span>
            </div>
          )}
        </div>

        {/* Candidate info */}
        {!collapsed && (
          <div className="mx-3 mt-3 p-3 rounded-lg bg-secondary/50 border border-secondary">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground text-xs font-bold">AK</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">Anika Krishnan</p>
                <p className="text-xs text-muted-foreground">Academic · Exam: 28 Aug</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              <Target size={11} className="text-accent flex-shrink-0" />
              <span className="text-xs text-muted-foreground">Current band:</span>
              <span className="text-xs font-bold text-primary tabular-nums">7.5</span>
              <span className="text-xs text-muted-foreground ml-auto">→ 8.0</span>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto scrollbar-thin">
          {!collapsed && (
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">Training Stages</p>
          )}
          {NAV_ITEMS.map((item) => (
            <div key={item.id} className="relative group">
              <Link
                href={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive(item.href)
                    ? 'sidebar-nav-active' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                } ${collapsed ? 'justify-center px-2' : ''}`}
              >
                <span className={`flex-shrink-0 ${isActive(item.href) ? 'text-primary' : ''}`}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge && (
                      <span className={badgeClass(item.badgeVariant)}>{item.badge}</span>
                    )}
                  </>
                )}
              </Link>
              {/* Collapsed tooltip */}
              {collapsed && (
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <div className="bg-foreground text-background text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-elevated">
                    {item.label}
                    {item.badge && <span className="ml-1.5 opacity-70">· {item.badge}</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="border-t border-border px-2 py-3 space-y-0.5">
          {[
            { id: 'nav-notif', icon: <Bell size={18} />, label: 'Notifications', badge: '2' },
            { id: 'nav-settings', icon: <Settings size={18} />, label: 'Settings' },
            { id: 'nav-logout', icon: <LogOut size={18} />, label: 'Sign Out' },
          ].map((item) => (
            <div key={item.id} className="relative group">
              <button
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${
                  collapsed ? 'justify-center px-2' : ''
                }`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge && (
                      <span className="bg-danger text-danger-foreground text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </button>
              {collapsed && (
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <div className="bg-foreground text-background text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-elevated">
                    {item.label}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Collapse toggle */}
          <button
            onClick={onToggleCollapse}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 ${
              collapsed ? 'justify-center px-2' : ''
            }`}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="flex-shrink-0">
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </span>
            {!collapsed && <span className="flex-1 text-left">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-card border-r border-border flex flex-col lg:hidden transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <div>
              <span className="font-bold text-foreground text-sm block">IELTSNoah</span>
              <span className="text-xs text-muted-foreground">Band 8+ Reading</span>
            </div>
          </div>
          <button
            onClick={onMobileClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close navigation"
          >
            <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto scrollbar-thin">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.id}
              href={item.href}
              onClick={onMobileClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive(item.href)
                  ? 'sidebar-nav-active' :'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className={`flex-shrink-0 ${isActive(item.href) ? 'text-primary' : ''}`}>
                {item.icon}
              </span>
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge && (
                <span className={badgeClass(item.badgeVariant)}>{item.badge}</span>
              )}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
}